import {
  collection,
  collectionGroup,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp,
  getDoc,
  limit,
  DocumentData,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { CalendarEvent, Appointment, Alert, MedicationReminder } from '../types/calendar';
import { emailService } from './emailService';
import { getAuth } from 'firebase/auth';
import { logger } from './logger';
import { handleError } from '../utils/errorUtils';

function petEventsCollection(petId: string) {
  return collection(db, `pets/${petId}/calendarEvents`);
}

function userRemindersCollection(ownerId: string) {
  return collection(db, `users/${ownerId}/reminders`);
}

const log = logger.createScope('CalendarService');

const safeGetDocs = async <T>(queryPromise: Promise<T>, label: string) => {
  try {
    return await queryPromise;
  } catch (error) {
    log.warn(`Failed to query ${label}`, { error: error as Error });
    return null;
  }
};

export const calendarService = {
  // Get all events for a specific pet
  getPetEvents: async (petId: string): Promise<CalendarEvent[]> => {
    const q = query(petEventsCollection(petId), orderBy('startTime', 'asc'));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs
      .map(doc => {
        const data = doc.data();
        // Convert Firestore Timestamps to ISO strings for reminderDate
        const reminderDate = data.reminderDate?.toDate 
          ? data.reminderDate.toDate().toISOString() 
          : data.reminderDate;
        
        return {
          id: doc.id,
          ...data,
          // Ensure reminder fields are properly converted
          reminderReceived: data.reminderReceived || false,
          reminderDate: reminderDate || undefined,
          reminderEmailId: data.reminderEmailId || undefined,
        } as CalendarEvent;
      })
      .filter(event => !(event.status as string) || (event.status as string) !== 'deleted');
  },

  // Get a specific event by ID
  getEventById: async (eventId: string): Promise<CalendarEvent | null> => {
    try {
      // Not used in most flows; if needed, callers should provide petId for direct path
      log.warn('getEventById: pet-scoped paths now required. This function should be updated to include petId.', {
        eventId
      });
      return null;
    } catch (error) {
      log.error('Error fetching event by ID', error as Error, { eventId });
      return null;
    }
  },

  // Get events for a specific date range
  getEventsByDateRange: async (
    startDate: string,
    endDate: string,
    ownerId: string,
    selectedPetId?: string | null
  ): Promise<CalendarEvent[]> => {
    log.debug('Fetching events for date range', {
      startDate,
      endDate,
      ownerId,
      selectedPetId
    });
    
    // Query calendar events
    const eventsQuery = selectedPetId
      ? query(
          petEventsCollection(selectedPetId),
          where('startTime', '>=', startDate),
          where('startTime', '<=', endDate),
          orderBy('startTime', 'asc')
        )
      : query(
          collectionGroup(db, 'calendarEvents'),
          where('ownerId', '==', ownerId),
          where('startTime', '>=', startDate),
          where('startTime', '<=', endDate),
          orderBy('startTime', 'asc')
        );
    
    // Query reminders collections (user-scoped preferred, legacy fallback)
    const userRemindersQuery = query(
      userRemindersCollection(ownerId),
      where('startTime', '>=', startDate),
      where('startTime', '<=', endDate),
      orderBy('startTime', 'asc')
    );
    const legacyRemindersQuery = query(
      collection(db, 'reminders'),
      where('ownerId', '==', ownerId),
      where('startTime', '>=', startDate),
      where('startTime', '<=', endDate),
      orderBy('startTime', 'asc')
    );
    
    try {
      // Execute both queries in parallel
      const [eventsSnapshot, userRemindersSnapshot, legacyRemindersSnapshot] = await Promise.all([
        getDocs(eventsQuery),
        safeGetDocs(getDocs(userRemindersQuery), 'user reminders'),
        safeGetDocs(getDocs(legacyRemindersQuery), 'legacy reminders')
      ]);
      
      // Map calendar events (when query was pet-scoped, doc may not have petId; set it so filter below keeps the event)
      const calendarEvents = eventsSnapshot.docs
        .map(doc => {
          const data = doc.data();
          log.debug('Raw Firestore data for event', { 
            eventId: doc.id, 
            eventType: data.type,
            hasReminderReceived: !!data.reminderReceived,
            reminderDate: data.reminderDate,
            reminderEmailId: data.reminderEmailId
          });
          
          // Convert Firestore Timestamps to ISO strings for reminderDate
          const reminderDate = data.reminderDate?.toDate 
            ? data.reminderDate.toDate().toISOString() 
            : data.reminderDate;
          
          return {
            id: doc.id,
            ...data,
            // When we queried by selectedPetId, events are for that pet; ensure petId is set so filter doesn't drop them
            ...(selectedPetId && !data.petId ? { petId: selectedPetId } : {}),
            // Ensure reminder fields are properly converted
            reminderReceived: data.reminderReceived || false,
            reminderDate: reminderDate || undefined,
            reminderEmailId: data.reminderEmailId || undefined,
          } as CalendarEvent;
        })
        .filter(event => !(event.status as string) || (event.status as string) !== 'deleted');
      
      // Map reminders
      const reminderEvents = [
        ...(userRemindersSnapshot?.docs || []),
        ...(legacyRemindersSnapshot?.docs || [])
      ]
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }) as CalendarEvent)
        .filter(event => !(event.status as string) || (event.status as string) !== 'deleted');
      
      // Combine all events
      let allEvents = [...calendarEvents, ...reminderEvents];
      
      // Filter by selected pet if provided
      if (selectedPetId) {
        log.debug('Filtering events by pet', { selectedPetId });
        allEvents = allEvents.filter(event => event.petId === selectedPetId);
      }
      
      // Sort events by start time
      allEvents = allEvents.sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
      
      log.info('Events fetched successfully', {
        calendarEventsCount: eventsSnapshot.docs.length,
        remindersCount: reminderEvents.length,
        totalEvents: allEvents.length,
        selectedPetId
      });
      
      return allEvents;
    } catch (error) {
      handleError(error, { showToUser: false, context: { operation: 'getEventsByDateRange', userId: ownerId } });
      // Fallback to just calendar events if reminders query fails
      const querySnapshot = await getDocs(eventsQuery);
      let fallbackEvents = querySnapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data(),
        }) as CalendarEvent)
        .filter(event => !(event.status as string) || (event.status as string) !== 'deleted');
      
      // Filter by selected pet if provided
      if (selectedPetId) {
        fallbackEvents = fallbackEvents.filter(event => event.petId === selectedPetId);
      }
      
      return fallbackEvents;
    }
  },

  // Add a new event
  addEvent: async (event: Omit<CalendarEvent, 'id' | 'createdAt' | 'updatedAt'>): Promise<CalendarEvent> => {
    const now = Timestamp.now().toDate().toISOString();
    // Ensure ownerId is set
    let ownerId = event.ownerId;
    if (!ownerId) {
      const auth = getAuth();
      ownerId = auth.currentUser?.uid || '';
      if (!ownerId) {
        log.warn('No ownerId found for event. This may cause Firestore permission errors.', {
          petId: event.petId,
          eventType: event.type
        });
      }
    }
    
    // Validate petId is present
    if (!event.petId) {
      throw new Error('petId is required to create a calendar event');
    }
    
    const eventData = {
      ...event,
      ownerId,
      createdAt: now,
      updatedAt: now,
    };

    const collectionPath = `pets/${event.petId}/calendarEvents`;
    log.debug('Creating calendar event in pet-scoped collection', {
      collectionPath,
      petId: event.petId,
      ownerId: ownerId,
      title: event.title,
      eventType: event.type,
      startTime: event.startTime
    });

    const docRef = await addDoc(petEventsCollection(event.petId), eventData);
    log.info('Calendar event created successfully (single source of truth)', {
      eventId: docRef.id,
      collectionPath,
      petId: event.petId,
      eventType: event.type
    });
    
    // Trigger notification if appointment is auto-created from provider email
    if (event.type === 'appointment' && event.autoCreated && ownerId) {
      try {
        const NotificationTriggerService = (await import('./notificationTriggerService')).default;
        await NotificationTriggerService.notifyAppointment({
          petId: event.petId,
          ownerId: ownerId,
          appointmentId: docRef.id,
          startTime: event.startTime,
          title: event.title,
          location: event.location,
          vetName: event.vetName
        });
      } catch (notifError) {
        log.warn('Failed to create appointment notification (non-critical)', {
          error: notifError as Error,
          eventId: docRef.id
        });
      }
    }
    
    const newEvent = {
      id: docRef.id,
      ...eventData,
    } as CalendarEvent;

    // Send email notification based on event type (don't fail save if notification fails)
    try {
      await sendEventNotification(newEvent);
      log.info('Email notification sent successfully', { eventId: docRef.id });
    } catch (notificationError) {
      log.error('Error sending email notification (event still saved successfully)', notificationError as Error, {
        eventId: docRef.id
      });
      // Don't throw - the event was saved successfully, just the notification failed
    }

    // Note: We no longer create notifications for new appointments (not action items)
    // Only appointment reminders will trigger notifications

    // Dispatch events to notify all components
    document.dispatchEvent(new CustomEvent('calendarEventCreated', {
      detail: { 
        eventId: newEvent.id, 
        newEvent,
        petId: newEvent.petId
      }
    }));
    document.dispatchEvent(new CustomEvent('refreshAppointments', { 
      detail: { petId: newEvent.petId } 
    }));
    document.dispatchEvent(new CustomEvent('refreshCalendarEvents'));

    return newEvent;
  },

  // Update an existing event
  updateEvent: async (
    id: string,
    eventData: Partial<CalendarEvent>,
    petIdOverride?: string
  ): Promise<void> => {
    const petId = petIdOverride || eventData.petId;
    try {
      log.debug('Updating event', { eventId: id, fields: Object.keys(eventData) });
      
      if (!petId) throw new Error('updateEvent requires petId in eventData');
      const docRef = doc(db, `pets/${petId}/calendarEvents/${id}`);
      
      // Check if document exists first
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        throw new Error(`Event with ID ${id} does not exist`);
      }
      
      const now = Timestamp.now().toDate().toISOString();
      
      // Create a clean update object without undefined values
      const updateData = {
        ...Object.fromEntries(
          Object.entries(eventData).filter(([_, v]) => v !== undefined)
        ),
        updatedAt: now,
      };
      
      log.debug('Provider fields in update data', {
        eventId: id,
        vetName: (updateData as Partial<CalendarEvent>).vetName,
        vetContact: (updateData as Partial<CalendarEvent>).vetContact,
        vetEmail: (updateData as Partial<CalendarEvent>).vetEmail,
        location: (updateData as Partial<CalendarEvent>).location
      });
      
      await updateDoc(docRef, updateData);
      log.info('Successfully updated event', { eventId: id, petId });

      // If the event is an appointment and it's being updated, send a notification
      if (eventData.type === 'appointment') {
        const updatedDocSnap = await getDoc(docRef);
        if (updatedDocSnap.exists()) {
          const event = { id: updatedDocSnap.id, ...updatedDocSnap.data() } as CalendarEvent;
          try {
            await sendEventNotification(event);
            log.info('Sent notification for updated event', { eventId: id });
          } catch (notificationError) {
            log.error('Error sending notification', notificationError as Error, { eventId: id });
            // Don't fail the update if notification fails
          }
        }
      }

      // Dispatch events to notify all components
      document.dispatchEvent(new CustomEvent('calendarEventUpdated', {
        detail: { 
          eventId: id, 
          updatedEvent: { id, ...eventData },
          statusChanged: eventData.status !== undefined,
          petId: eventData.petId
        }
      }));
      
      if (eventData.status === 'completed') {
        document.dispatchEvent(new CustomEvent('calendarEventCompleted', {
          detail: { 
            eventId: id, 
            completedEvent: { id, ...eventData }
          }
        }));
      }
      
      document.dispatchEvent(new CustomEvent('refreshAppointments', { 
        detail: { petId: eventData.petId } 
      }));
      document.dispatchEvent(new CustomEvent('refreshCalendarEvents'));
    } catch (error) {
      handleError(error, { showToUser: false, context: { operation: 'updateEvent', petId } });
      throw error;
    }
  },

  // Delete an event
  deleteEvent: async (id: string, petId?: string): Promise<void> => {
    try {
      log.debug('Attempting to delete event', { eventId: id, petId });
      
      if (!petId) throw new Error('deleteEvent requires petId');
      const docRef = doc(db, `pets/${petId}/calendarEvents/${id}`);
      
      // Check if document exists first
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        log.error(`Event with ID ${id} does not exist`, undefined, { eventId: id, petId });
        throw new Error(`Event with ID ${id} not found`);
      }
      
      const eventData = docSnap.data();
      log.debug('Found event to delete', {
        eventId: id,
        title: eventData.title,
        eventType: eventData.type
      });
      
      // Try direct deletion first
      try {
        await deleteDoc(docRef);
        log.info('Successfully deleted event from Firestore', { eventId: id, petId });
      } catch (deleteError: unknown) {
        const deleteCode = deleteError instanceof Error && 'code' in deleteError ? (deleteError as Error & { code?: string }).code : undefined;
        log.warn(`Direct deletion failed (${deleteCode}), trying to mark as deleted`, {
          error: deleteError,
          eventId: id,
          petId
        });
        
        // Alternative: Mark as deleted instead of actual deletion
        try {
          await updateDoc(docRef, {
            status: 'deleted',
            deletedAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          });
          log.info('Event marked as deleted in Firestore', { eventId: id, petId });
        } catch (updateError: unknown) {
          const updateMessage = updateError instanceof Error ? updateError.message : String(updateError);
          log.error('Failed to mark event as deleted', updateError instanceof Error ? updateError : new Error(updateMessage), { eventId: id, petId });
          throw new Error(`Failed to delete event: ${updateMessage}`);
        }
      }

      // Dispatch events to notify all components
      document.dispatchEvent(new CustomEvent('calendarEventDeleted', {
        detail: { 
          eventId: id,
          petId
        }
      }));
      document.dispatchEvent(new CustomEvent('refreshAppointments', { 
        detail: { petId } 
      }));
      document.dispatchEvent(new CustomEvent('refreshCalendarEvents'));
    } catch (error: unknown) {
      handleError(error, { showToUser: false, context: { operation: 'deleteEvent', petId } });
      throw error;
    }
  },

  // Get upcoming events
  getUpcomingEvents: async (ownerId: string, limitCount: number = 5, selectedPetId?: string | null): Promise<CalendarEvent[]> => {
    const now = new Date().toISOString();
    log.debug('Fetching upcoming events', {
      ownerId,
      selectedPetId,
      limitCount,
      currentTime: now
    });
    
    // Query calendar events collection
    const eventsQuery = query(
      collectionGroup(db, 'calendarEvents'),
      where('ownerId', '==', ownerId),
      where('startTime', '>=', now),
      orderBy('startTime', 'asc'),
      limit(limitCount * 2)
    );
    
    // Query reminders collections (user-scoped preferred, legacy fallback)
    const userRemindersQuery = query(
      userRemindersCollection(ownerId),
      where('startTime', '>=', now),
      orderBy('startTime', 'asc'),
      limit(limitCount * 2)
    );
    const legacyRemindersQuery = query(
      collection(db, 'reminders'),
      where('ownerId', '==', ownerId),
      where('startTime', '>=', now),
      orderBy('startTime', 'asc'),
      limit(limitCount * 2)
    );
    
    try {
      // Execute both queries in parallel
      const [eventsSnapshot, userRemindersSnapshot, legacyRemindersSnapshot] = await Promise.all([
        getDocs(eventsQuery),
        safeGetDocs(getDocs(userRemindersQuery), 'user reminders'),
        safeGetDocs(getDocs(legacyRemindersQuery), 'legacy reminders')
      ]);
      
      log.debug('Raw query returned events', {
        calendarEventsCount: eventsSnapshot.docs.length,
        remindersCount: (userRemindersSnapshot?.docs.length || 0) + (legacyRemindersSnapshot?.docs.length || 0)
      });
      
      // Map and filter calendar events
      let calendarEvents = eventsSnapshot.docs
        .map(d => {
          const data = d.data() as DocumentData;
          return { id: d.id, ...data } as CalendarEvent;
        })
        .filter(event => event.status === 'scheduled');
      
      // Map and filter reminders
      let reminderEvents = [
        ...(userRemindersSnapshot?.docs || []),
        ...(legacyRemindersSnapshot?.docs || [])
      ]
        .map(d => {
          const data = d.data() as DocumentData;
          return { id: d.id, ...data } as CalendarEvent;
        })
        .filter(event => event.status === 'scheduled');
      
      // Filter by selected pet if provided
      if (selectedPetId) {
        log.debug('Filtering upcoming events by pet', { selectedPetId });
        calendarEvents = calendarEvents.filter(event => event.petId === selectedPetId);
        reminderEvents = reminderEvents.filter(event => event.petId === selectedPetId);
      }
      
      // Combine, sort, and limit
      const allEvents = [...calendarEvents, ...reminderEvents]
        .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())
        .slice(0, limitCount);
      
      log.info('Upcoming events fetched successfully', {
        totalEvents: allEvents.length,
        selectedPetId
      });
      return allEvents;
    } catch (error: unknown) {
      // Only log non-permission errors
      const errorCode = (error as { code?: string }).code;
      if (errorCode !== 'permission-denied' && errorCode !== 'PERMISSION_DENIED') {
        handleError(error, { showToUser: false, context: { operation: 'getUpcomingEvents', userId: ownerId } });
      }
      // Fallback to just calendar events
      const querySnapshot = await getDocs(eventsQuery);
      log.debug('Fallback query returned events', {
        eventsCount: querySnapshot.docs.length
      });
      
      let filteredEvents = querySnapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        } as CalendarEvent))
        .filter(event => event.status === 'scheduled');
      
      // Filter by selected pet if provided
      if (selectedPetId) {
        filteredEvents = filteredEvents.filter(event => event.petId === selectedPetId);
      }
      
      filteredEvents = filteredEvents.slice(0, limitCount);
      
      log.info('Fallback result', { eventsCount: filteredEvents.length });
      return filteredEvents;
    }
  },

  // Get recent past events (for debugging/viewing completed appointments)
  getRecentEvents: async (ownerId: string, limitCount: number = 5): Promise<CalendarEvent[]> => {
    const now = new Date().toISOString();
    const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
    
    log.debug('Fetching recent events', {
      ownerId,
      dateRange: { start: thirtyDaysAgo, end: now },
      limitCount
    });
    
    const q = query(
      collectionGroup(db, 'calendarEvents'),
      where('ownerId', '==', ownerId),
      where('startTime', '>=', thirtyDaysAgo),
      where('startTime', '<=', now),
      orderBy('startTime', 'desc'),
      limit(limitCount)
    );
    
    const querySnapshot = await getDocs(q);
    log.debug('Recent events query returned', {
      eventsCount: querySnapshot.docs.length
    });
    
    const events = querySnapshot.docs
      .map(d => {
        const data = d.data() as DocumentData;
        return { id: d.id, ...data } as CalendarEvent;
      })
      .filter(event => !(event.status as string) || (event.status as string) !== 'deleted');
    
    log.info('Recent events after filtering', { eventsCount: events.length });
    return events;
  },
};

// Helper function to send event notifications
async function sendEventNotification(event: CalendarEvent): Promise<void> {
  const petDoc = await getDoc(doc(db, 'pets', event.petId));
  const pet = petDoc.data();
  if (!pet) {
    log.warn(`Pet not found for event.petId=${event.petId}. Skipping notification.`, {
      eventId: event.id,
      petId: event.petId
    });
    return;
  }
  const userDoc = await getDoc(doc(db, 'users', event.ownerId));
  const user = userDoc.data();
  if (!user) {
    log.warn(`User not found for event.ownerId=${event.ownerId}. Skipping notification.`, {
      eventId: event.id,
      ownerId: event.ownerId
    });
    return;
  }

  switch (event.type) {
    case 'appointment':
      await emailService.sendAppointmentReminder(user.email, {
        petName: pet.name,
        date: new Date(event.startTime).toLocaleDateString(),
        time: new Date(event.startTime).toLocaleTimeString(),
        type: (event as Appointment).appointmentType,
        vetName: (event as Appointment).vetName,
      });
      break;

    case 'medication':
      await emailService.sendMedicationReminder(user.email, {
        petName: pet.name,
        medication: (event as MedicationReminder).medicationName,
        dosage: (event as MedicationReminder).dosage,
        frequency: (event as MedicationReminder).frequency,
        nextDose: (event as MedicationReminder).nextDose || event.startTime,
      });
      break;

    case 'alert':
      await emailService.sendHealthAlert(user.email, {
        petName: pet.name,
        alertType: (event as Alert).alertType,
        message: event.description,
        severity: (event as Alert).severity,
      });
      break;
  }
} 